<template>
  <div class="tw:relative">
    <div class="tw:overflow-x-auto tw:w-full" ref="tableContainer" @scroll="updateShadow">
      <table class="tw:w-full tw:border-0 tw:text-sm tw:text-gray-800 tw:border-separate tw:border-spacing-0">
        <thead class="tw:table-header-group tw:text-xs tw:text-gray-800 tw:bg-white">
          <tr class="tw:border-b-1 tw:px-6 tw:py-5">
            <th
              v-for="column in columns"
              :key="column?.id"
              scope="col"
              class="tw:px-4 tw:py-5 tw:relative tw:border-l-0 tw:border-y-gray-500 tw:text-nowrap"
              :class="[getHeaderClasses(column), hasShadow ? 'table-shadow' : 'divider']"
              @click="sortTable(column)"
              @keydown.enter="sortTable(column)"
            >
              <span class="tw:flex tw:items-center tw:relative nowrap">
                {{ $t(column.header).replace(/-/g, '&#x2011;') }}
                <div v-if="column.header.length > 1 && column.sortable" class="tw:relative">
                  <ChevronUp
                    class="tw:ml-2 tw:w-[11px] tw:h-[10px]"
                    v-if="
                      column.id !== defaultSortableColumn ||
                      (column.id === defaultSortableColumn && sortingType === 'ascending')
                    "
                    :class="
                      column.id === defaultSortableColumn && sortingType === 'ascending'
                        ? 'tw:stroke-blue-700'
                        : 'tw:stroke-gray-800'
                    "
                  />
                  <ChevronDown
                    class="tw:ml-2 tw:w-[11px] tw:h-1"
                    v-if="
                      column.id !== defaultSortableColumn ||
                      (column.id === defaultSortableColumn && sortingType === 'descending')
                    "
                    :class="
                      column.id === defaultSortableColumn && sortingType === 'descending'
                        ? 'tw:stroke-blue-700'
                        : 'tw:stroke-gray-800'
                    "
                  />
                </div>
              </span>
            </th>
          </tr>
        </thead>
        <tbody v-if="!isLoading">
          <template v-for="row in getData" :key="`${row.createdAt}-${row.boostedDates}-${row.commission}`">
            <tr class="tw:text-gray-800 tw:bg-white table-row">
              <td
                v-for="column in columns"
                :key="column.id"
                scope="row"
                class="cell tw:px-3 tw:py-4 tw:whitespace-nowrap tw:border-l-0 tw:border-t-1 tw:border-b-1 tw:relative tw:border-gray-200"
                :class="[getClass(column), hasShadow ? 'table-shadow' : 'divider']"
              >
                <div>
                  <span
                    v-if="column.id === 'deactivationReason'"
                    class="badge"
                    :class="getStatusBadgeClass(row.deactivationReason)"
                  >
                    {{ $t(row.deactivationReason) }}
                  </span>
                  <span v-else :class="{ 'tw:font-bold': column.id === 'boostedDates' }">
                    {{ row[column.id] }}
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <span
        v-if="isLoading"
        class="tw:flex tw:px-3 tw:py-4 tw:border-b tw:border-gray-200 tw:w-full tw:items-center tw:justify-center"
        ><SmallDotLoader color="#0079C2"
      /></span>

      <div v-if="hasShadow && !isScrolledToRight" class="scroll-shadow"></div>
    </div>
    <div class="tw:flex tw:justify-center tw:text-sm tw:items-center tw:px-6 tw:py-5">
      <GhostButton
        size="medium"
        :disabled="loadedElements === tableRows.length || loadedElements > tableRows.length"
        @click="loadMore()"
        @keydown.enter="loadMore()"
      >
        {{ $t('show_more') }}
      </GhostButton>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, onUpdated, computed } from 'vue'

import ChevronUp from '@/components/icons/ChevronUp.svg?component'
import ChevronDown from '@/components/icons/ChevronDown.svg?component'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'

type BoostHistoryStatusKey =
  | 'rc_provider_status_active'
  | 'rc_smartboost_expired'
  | 'rc_smartboost_canceled'
  | 'rc_smartboost_commission_updated'
  | 'rc_smartboost_base_commission_updated'

type BoostHistoryStatusBadgeClass = 'tw:bg-green-200' | 'tw:bg-red-200' | 'tw:bg-gray-200'

interface BoostHistoryTableRow {
  createdAt: string
  validUntil: string
  deactivationReason: BoostHistoryStatusKey
  boostedDates: string
  commission: string
}

interface BoostHistoryTableColumn {
  id: string
  header: string
  colType: string
  sortable: boolean
  className: string
}

const props = withDefaults(
  defineProps<{
    columns?: BoostHistoryTableColumn[]
    data?: BoostHistoryTableRow[]
    isLoading?: boolean
  }>(),
  {
    columns: () => [],
    data: () => [],
    isLoading: false,
  }
)

const getStatusBadgeClass = (statusKey: BoostHistoryStatusKey): BoostHistoryStatusBadgeClass => {
  if (statusKey === 'rc_provider_status_active') return 'tw:bg-green-200'
  if (statusKey === 'rc_smartboost_canceled') return 'tw:bg-red-200'
  return 'tw:bg-gray-200'
}

const defaultSortableColumn = ref('validUntil')
const loadedElements = ref(props.data.length && props.data.length < 6 ? props.data.length : 6)
const sortingType = ref('ascending')
const tableRows = ref<BoostHistoryTableRow[]>(props.data)
const tableContainer = ref<HTMLElement | null>(null)
const hasShadow = ref(false)
const isScrolledToRight = ref(false)

const getData = computed(() => {
  return tableRows.value.slice(0, loadedElements.value)
})

watch(
  () => props.data,
  newData => {
    tableRows.value = newData
  },
  { deep: true, immediate: true }
)

const updateShadow = () => {
  if (tableContainer.value) {
    hasShadow.value = tableContainer.value.scrollWidth > tableContainer.value.clientWidth
    isScrolledToRight.value =
      Math.round(tableContainer.value.scrollLeft) + Math.round(tableContainer.value.clientWidth) + 1 >=
      Math.round(tableContainer.value.scrollWidth)
  }
}

onUpdated(() => updateShadow())

onMounted(() => {
  updateShadow()

  window.addEventListener('resize', updateShadow)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateShadow)
})

const getClass = (column: BoostHistoryTableColumn) => {
  let classes = [`${column.className}`]
  if (column.id === defaultSortableColumn?.value) {
    classes.push('tw:md:text-blue-700')
  }

  if (column.id === 'activeFrom' && hasShadow.value) {
    classes.push('sticky')
  } else if (column.id === 'activeFrom' && !hasShadow.value) {
    classes.push('sticky-scroll')
  } else {
    classes.push('tw:border-r-0')
  }
  return classes.toString().replace(/,/g, ' ')
}

const loadMore = () => {
  if (tableRows.value.length > loadedElements.value) {
    if (tableRows.value.length > loadedElements.value + 10) {
      loadedElements.value = loadedElements.value + 10
    } else {
      loadedElements.value = loadedElements.value + (tableRows.value.length - loadedElements.value)
    }
  }
}

const sortTable = (col: BoostHistoryTableColumn) => {
  if (col.sortable) {
    if (defaultSortableColumn.value === col.id) {
      sortingType.value = sortingType.value === 'ascending' ? 'descending' : 'ascending'
    } else {
      sortingType.value = 'ascending'
      defaultSortableColumn.value = col.id
    }

    let sortTypeTemp = sortingType.value

    tableRows.value = [...tableRows.value].sort(function (a, b) {
      if (col.id === 'date') {
        if (a[col.id].start > b[col.id].start) {
          return sortTypeTemp === 'ascending' ? 1 : -1
        } else if (a[col.id].start < b[col.id].start) {
          return sortTypeTemp === 'ascending' ? -1 : 1
        }
      } else if (col.colType === 'date') {
        if (new Date(a[col.id]) > new Date(b[col.id])) {
          return sortTypeTemp === 'ascending' ? 1 : -1
        } else if (new Date(a[col.id]) < new Date(b[col.id])) {
          return sortTypeTemp === 'ascending' ? -1 : 1
        }
      } else {
        if (a[col.id] > b[col.id]) {
          return sortTypeTemp === 'ascending' ? 1 : -1
        } else if (a[col.id] < b[col.id]) {
          return sortTypeTemp === 'ascending' ? -1 : 1
        }
      }
      return 0
    })
  }
}

const getHeaderClasses = (column: BoostHistoryTableColumn) => {
  let classes = []

  if (column.sortable) {
    classes.push('table-header')
  }
  if (column.id === defaultSortableColumn.value) {
    classes.push('tw:text-blue-700 tw:bg-blue-100 ')
  }
  if (column.id === 'status' && props.columns.length > 6) {
    classes.push('tw:min-w-[140px] tw:w-[140px] tw:border-r-0')
  } else if (column.id === 'activeFrom' && hasShadow.value) {
    classes.push('sticky')
  } else if (column.id === 'activeFrom' && !hasShadow.value) {
    classes.push('sticky-scroll')
  } else {
    classes.push('tw:border-r-0')
  }
  return classes.join(' ')
}
</script>
<style scoped>
table thead th,
table tbody td {
  font-size: 14px;
}

table::after {
  position: absolute;
  width: 100%;
  height: 1px;
  background-color: var(--tw-color-gray-300);
  display: block;
  content: '';
}

.table-header:hover {
  cursor: pointer;
  background-color: var(--tw-color-blue-100);
}
.icon-fill {
  fill: var(--tw-color-blue-700);
}

.top-sort-arrow {
  right: 0;
  top: -1px;
}

.bottom-sort-arrow {
  right: 0;
  top: 6px;
}
.table-row:hover .cell {
  background-color: var(--tw-color-blue-100);
}

.bottom-shadow {
  box-shadow: 0 3px 3px -1px rgba(0, 0, 0, 0.22);
  transform: scale(1);
  padding-right: 2.75rem;
}

.sticky {
  position: sticky;
  left: 0;
  z-index: 10;
  min-width: 150px;
  width: 150px;
  border-right: 0px;
  background-color: var(--tw-color-white);
  transition: box-shadow 0.3s ease;
}

.sticky-scroll {
  position: sticky;
  left: 0;
  z-index: 10;
  min-width: 150px;
  width: 150px;
  border-right: 0;
  transition: box-shadow 0.3s ease;
}
.sticky.table-shadow::after {
  content: '';
  position: absolute;
  top: 0;
  right: -8px;
  width: 8px;
  height: 100%;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.09), rgba(0, 0, 0, 0));
  pointer-events: none;
}

.sticky-scroll.divider::after {
  border-right: 1px solid var(--tw-color-gray-200);
  content: '';
  position: absolute;
  top: 0px;
  height: 100%;
  right: -1px;
}

.scroll-shadow {
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: calc(100% - 76px);
  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1)); /* White shadow */
  pointer-events: none;
}

.badge {
  border-radius: 50px;
  font-size: 0.875rem;
  position: relative;
  color: var(--tw-color-black);
  padding: 4px 12px;
  line-height: 1.43rem;
  @media (max-width: 768px) {
    padding: 0 12px;
  }
}

.cell {
  @media (max-width: 768px) {
    width: 100%;
  }
}
</style>
